import { ApolloError, gql, useQuery } from '@apollo/client'
import type { GetProfileReviewsQuery, PublicFeaturedReview } from 'apollo-gql'
import { currentMarketIdVar } from 'graphql/reactive'

import useRouter from './useRouter'

export const GET_REVIEWS_QUERY = gql`
  query GetProfileReviews(
    $marketId: ID!
    $slug: String!
    $first: Int
    $skip: Int
  ) {
    market(id: $marketId) {
      id
      profile(slug: $slug) {
        slug
        id
        name
        title
        profilePicture {
          id
          url(format: { width: 128, height: 128, fit: crop })
        }
        reviews(first: $first, skip: $skip) {
          totalCount
          edges {
            node {
              id
              ... on PublicFeaturedReview {
                id
                rating
                messageToProfile
                reviewedAt
              }
            }
          }
        }
        reviewCountByRating {
          count
          rating
        }
      }
    }
  }
`

type IUsePublicReviews = Pick<
  GetProfileReviewsQuery['market']['profile'],
  'reviewCountByRating' | 'name' | 'title' | 'profilePicture'
> & {
  reviews: Pick<
    PublicFeaturedReview,
    'id' | 'rating' | 'messageToProfile' | 'reviewedAt'
  >[]
  loading: boolean
  error: ApolloError
  totalCount: number
}

const usePublicReviews = (page = 0, limit = 10): IUsePublicReviews => {
  const router = useRouter()
  const { slug } = router.query
  const marketId = currentMarketIdVar()

  const variables = { slug, marketId, first: limit, skip: page * limit }

  const { loading, data, error } = useQuery<GetProfileReviewsQuery>(
    GET_REVIEWS_QUERY,
    {
      variables,
      returnPartialData: true,
    },
  )

  const reviews =
    data.market?.profile?.reviews?.edges.map((edge) => edge.node) || []

  const {
    title,
    profilePicture,
    name,
    reviewCountByRating = [],
  } = data?.market?.profile
  return {
    reviews,
    title,
    profilePicture,
    name,
    reviewCountByRating,
    loading,
    error,
    totalCount: data.market?.profile?.reviews?.totalCount,
  }
}

export default usePublicReviews
